import { useState } from 'react'
import { useNavigate } from 'react-router'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Dialog, DialogClose, DialogContent } from '@/components/ui/dialog'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import type { TicketDetail } from '@/lib/api/types'
import { errorMessage } from '@/lib/api/errors'
import { useFollowUp } from './api'

export function NewTicketDialog({
  ticket,
  open,
  onOpenChange,
}: {
  ticket: TicketDetail
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        title="Create a new ticket"
        description={`Send a separate update to ${ticket.requester?.email ?? 'this requester'}. ${ticket.reference} stays resolved.`}
      >
        {open && <NewTicketForm ticket={ticket} onDone={() => onOpenChange(false)} />}
      </DialogContent>
    </Dialog>
  )
}

function NewTicketForm({ ticket, onDone }: { ticket: TicketDetail; onDone: () => void }) {
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const create = useFollowUp(ticket.id)
  const navigate = useNavigate()
  return (
    <form
      className="grid gap-4"
      onSubmit={(event) => {
        event.preventDefault()
        create.mutate(
          { subject: subject.trim(), body: body.trim() },
          {
            onSuccess: (next) => {
              onDone()
              toast.success('New ticket sent. The previous ticket stays resolved.')
              void navigate(`/tickets/${next.id}`)
            },
            onError: (error) => toast.error(errorMessage(error)),
          },
        )
      }}
    >
      <Field label="Subject" htmlFor="new-ticket-subject" required>
        <Input
          id="new-ticket-subject"
          value={subject}
          onChange={(event) => setSubject(event.target.value)}
          maxLength={150}
        />
      </Field>
      <Field label="Message" htmlFor="new-ticket-body" required>
        <Textarea
          id="new-ticket-body"
          value={body}
          onChange={(event) => setBody(event.target.value)}
          rows={6}
          maxLength={10000}
        />
      </Field>
      <p className="text-xs text-ink-subtle">
        The requester receives this message by email{ticket.workspace ? ' and in the app' : ''}. The new
        ticket will resolve after 24 hours unless they reply.
      </p>
      <div className="flex justify-end gap-2">
        <DialogClose asChild>
          <Button variant="ghost">Cancel</Button>
        </DialogClose>
        <Button
          type="submit"
          disabled={subject.trim().length < 2 || body.trim().length < 2}
          loading={create.isPending}
        >
          Send new ticket
        </Button>
      </div>
    </form>
  )
}
